/*
 * glaze-photo-sync.js
 *
 * Sincroniza a galeria do PDP com o esmalte selecionado no widget do app Avis
 * Product Options (avp-options).
 *
 * As fotos de variação seguem a convenção de filename
 * <produto>[-tamanho-<tamanho>]-argila-<argila>-esmalte-<esmalte>.<ext> e chegam do
 * servidor anotadas com data-glaze-argila / data-glaze-esmalte / data-glaze-tamanho
 * (snippet glaze-media-attrs.liquid) e ocultas por padrão (component-glaze-media.css).
 * Ao selecionar um esmalte, este script revela e ativa a mídia da combinação tamanho +
 * argila (variantes Shopify) + esmalte (radio do app), preferindo a foto específica do
 * tamanho e caindo na genérica (sem -tamanho-) quando ele não tem foto própria; sem
 * correspondência, volta à primeira foto base.
 *
 * O app renderiza tarde e o Dawn re-renderiza a galeria na troca de variante, por isso:
 * - escuta de radios por delegação no document;
 * - re-aplicação via PUB_SUB_EVENTS.variantChange (com atraso para o app alternar o grupo
 *   de esmaltes da argila).
 *
 * Na troca de argila, a cor selecionada acompanha: o grupo novo recebe o mesmo esmalte
 * (quando existe nele) ou a primeira cor do grupo (ex.: Branco Amme). Sem cor escolhida
 * ainda, o grupo novo fica sem seleção (ex.: preselect da argila oficial no load).
 *
 * A combinação escolhida vai para a query string (?tamanho&argila&cor) e é restaurada
 * no load: variante pré-selecionada no picker do tema, cor pré-selecionada no app e
 * foto da combinação aplicada ANTES de o app renderizar (evita o flick foto base ->
 * foto da cor). O Dawn reescreve a URL só com ?variant= na troca de variante, por isso
 * os parâmetros são re-gravados após cada variantChange.
 */
(function () {
  'use strict';

  var ACTIVE = 'amme-glaze-active';
  var STOPWORDS = { de: true, da: true, do: true, das: true, dos: true };

  // Última cor escolhida (slug) e grupo de esmaltes visível: base do carry-over da cor
  // entre argilas. O grupo é rastreado por identidade de elemento — o app alterna o
  // style de elementos persistentes, e atribui o name dos radios DEPOIS do style, então
  // o name não é confiável no momento do toggle.
  var lastEsmalteSlug = null;
  var lastGroup = null;

  // Janela de reforço do carry-over: ao mostrar um grupo, o app o re-renderiza de novo
  // ~1s depois e restaura a seleção antiga DELE (sem evento change), atropelando a cor
  // transferida. Durante a janela, reapply() reafirma o alvo; clique real do usuário
  // (change isTrusted) cancela.
  var enforceGroup = null;
  var enforceSlug = null;
  var enforceUntil = 0;

  // Cor vinda da query string, aguardando o app renderizar o grupo de esmaltes para
  // ser marcada no radio correspondente. Só é limpa quando a seleção CONFIRMA (radio
  // marcado num reapply seguinte), por clique real do usuário ou pelo deadline — o
  // re-render do tema pós-preselect pode apagar a marcação sem evento nenhum.
  var pendingCor = null;
  var pendingCorUntil = 0;

  // Reproduz o slug do gerador de imagens: minúsculas, sem acentos, sem "de/da/do",
  // separadores viram hífen. Aplicado aos DOIS lados da comparação (valor do radio e
  // data attr), tolerando variações de acento/stopword entre app e filename.
  function slugify(text) {
    if (!text) return '';
    return String(text)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .split(/[^a-z0-9]+/)
      .filter(function (part) {
        return part && !STOPWORDS[part];
      })
      .join('-');
  }

  function gallery() {
    return document.querySelector('media-gallery');
  }

  function clearActive() {
    var actives = document.querySelectorAll('.' + ACTIVE);
    for (var i = 0; i < actives.length; i++) actives[i].classList.remove(ACTIVE);
  }

  // Promove a foto da combinação exibida a download imediato e prioridade alta. As fotos
  // de variação chegam do servidor com loading="lazy" (posição > 1 na galeria), mas a foto
  // visível do PDP é escolhida por JS (?cor= no load / troca de esmalte), então o browser
  // adiava o download da lazy revelada — causa do LCP alto. Só ajusta atributos, é barato
  // e idempotente (repetir na troca de cor não faz mal; a próxima foto é a nova LCP).
  function promoteImage(slide) {
    if (!slide) return;
    var img = slide.querySelector('img');
    if (!img) return;
    img.setAttribute('fetchpriority', 'high');
    if (img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
  }

  function resetSliders(gal) {
    var sliders = gal.querySelectorAll('slider-component');
    for (var i = 0; i < sliders.length; i++) {
      if (typeof sliders[i].resetPages === 'function') sliders[i].resetPages();
    }
  }

  // Argila entre parênteses no name do radio do app (ex.: "Cor (flocos)").
  function argilaFromRadio(radio) {
    var match = /\(([^)]+)\)/.exec(radio.name || '');
    return match ? slugify(match[1]) : null;
  }

  // Valor cru selecionado de uma opção do seletor de variantes do tema (por nome no
  // legend), ex.: "FLOCOS", "22CM x 17CM".
  function optionFromVariantPicker(namePattern) {
    var picker = document.querySelector('variant-selects');
    if (!picker) return null;
    var fieldsets = picker.querySelectorAll('fieldset');
    for (var i = 0; i < fieldsets.length; i++) {
      var legend = fieldsets[i].querySelector('legend');
      if (legend && namePattern.test(legend.textContent)) {
        var checked = fieldsets[i].querySelector('input:checked');
        if (checked) return checked.value;
      }
    }
    var selects = picker.querySelectorAll('select');
    for (var j = 0; j < selects.length; j++) {
      var label = selects[j].closest('.product-form__input');
      if (label && namePattern.test(label.textContent)) return selects[j].value;
    }
    return null;
  }

  // Fallback: valor selecionado da opção "Argila" no seletor de variantes do tema.
  function argilaFromVariantPicker() {
    return slugify(optionFromVariantPicker(/argila/i));
  }

  function tamanhoFromVariantPicker() {
    return slugify(optionFromVariantPicker(/tamanho/i)) || null;
  }

  // Grupo de esmaltes visível do app (o app alterna um grupo por argila).
  function visibleGlazeGroup() {
    var groups = document.querySelectorAll('.glaze-dropdown.avp-option');
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].offsetParent !== null) return groups[i];
    }
    return null;
  }

  // O tema (e o app) recriam o innerHTML do modal de zoom em alguns fluxos (ex.: troca de
  // variante), apagando a classe ativa dos itens do modal. Espelha o slide ativo da
  // galeria nos itens do modal; chamado no apply() e sempre que o modal abre.
  function syncModal() {
    var modalItems = document.querySelectorAll('.product-media-modal__content .' + ACTIVE);
    for (var i = 0; i < modalItems.length; i++) modalItems[i].classList.remove(ACTIVE);

    var activeSlide = document.querySelector('.product__media-item.' + ACTIVE);
    if (!activeSlide) return;
    // No modal o data-media-id é só o id numérico da mídia (sem prefixo da seção).
    var rawId = (activeSlide.getAttribute('data-media-id') || '').split('-').pop();
    var targets = document.querySelectorAll(
      '.product-media-modal__content [data-media-id="' + rawId + '"]'
    );
    for (var t = 0; t < targets.length; t++) targets[t].classList.add(ACTIVE);
  }

  function titleCase(text) {
    return String(text)
      .toLowerCase()
      .replace(/\S+/g, function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      });
  }

  // Frase sobre a galeria (product-media-gallery, photo_info: true). Com nomes, mostra a
  // combinação da foto exibida com o prefixo de IA ("A cor dessa foto foi simulada com
  // IA na..."); sem nomes, volta aos defaults dos metafields e ao prefixo da foto real
  // ("O produto dessa foto foi feito com...").
  function updatePhotoInfo(argilaName, esmalteName, simulated) {
    var box = document.querySelector('[data-photo-info]');
    if (!box) return;
    var argila = box.querySelector('[data-photo-info-argila]');
    var esmalte = box.querySelector('[data-photo-info-esmalte]');
    if (!argila || !esmalte) return;
    var argilaText = argilaName || box.getAttribute('data-default-argila');
    var esmalteText = esmalteName || box.getAttribute('data-default-esmalte');
    // Sem nomes (foto real de produto sem metafields): não há o que dizer — oculta.
    if (!argilaText || !esmalteText) {
      box.hidden = true;
      return;
    }
    box.hidden = false;
    argila.textContent = argilaText;
    esmalte.textContent = esmalteText;
    var prefix = box.querySelector('[data-photo-info-prefix]');
    if (prefix) {
      prefix.textContent = simulated
        ? box.getAttribute('data-prefix-ai')
        : box.getAttribute('data-prefix-real');
    }
  }

  // Frase conforme o slide exibido (navegação manual por setas/swipe/miniaturas): foto
  // de variação (data-glaze-*) é simulação de IA; qualquer outra é foto real (defaults).
  function updatePhotoInfoForSlide(slide) {
    if (!slide) return;
    var esmalteAttr = slide.getAttribute('data-glaze-esmalte');
    if (esmalteAttr) {
      var argilaSlug = slugify(slide.getAttribute('data-glaze-argila'));
      var esmalteSlug = slugify(esmalteAttr);
      updatePhotoInfo(displayArgila(argilaSlug), displayEsmalte(esmalteSlug), true);
    } else {
      updatePhotoInfo(null, null, false);
    }
  }

  // Nome de exibição do esmalte: valor do radio selecionado (preserva acentos, ex.
  // "Pedra Sabão"); fallback reconstrói do slug.
  function displayEsmalte(esmalteSlug) {
    var group = visibleGlazeGroup();
    var checked = group && group.querySelector('input[type="radio"]:checked');
    if (checked && slugify(checked.value) === esmalteSlug) return checked.value;
    return titleCase(esmalteSlug.replace(/-/g, ' '));
  }

  // Nome de exibição da argila: valor do picker do tema ("FLOCOS" -> "Flocos");
  // fallback reconstrói do slug.
  function displayArgila(argilaSlug) {
    var raw = optionFromVariantPicker(/argila/i);
    if (raw && slugify(raw) === argilaSlug) return titleCase(raw);
    return titleCase(argilaSlug.replace(/-/g, ' '));
  }

  // Espelha a combinação na query string (?tamanho&argila&cor, slugs) preservando os
  // demais parâmetros (ex.: ?variant= do tema) — o link completo restaura a seleção.
  // Debounce: o apply dispara em rajada e o replaceState tem rate-limit em browsers.
  var urlTimer = null;
  function scheduleUrlUpdate() {
    clearTimeout(urlTimer);
    urlTimer = setTimeout(function () {
      if (!window.history || !window.history.replaceState || !window.URLSearchParams) return;
      var group = visibleGlazeGroup();
      var checked = group && group.querySelector('input[type="radio"]:checked');
      var cor = checked ? slugify(checked.value) : lastEsmalteSlug;
      if (!cor) return; // a URL só carrega a combinação depois de uma cor escolhida
      var params = new URLSearchParams(window.location.search);
      var argila = argilaFromVariantPicker();
      var tamanho = tamanhoFromVariantPicker();
      if (argila) params.set('argila', argila);
      if (tamanho) params.set('tamanho', tamanho);
      params.set('cor', cor);
      window.history.replaceState({}, '', window.location.pathname + '?' + params.toString());
    }, 200);
  }

  // Marca no seletor de variantes do tema as opções vindas da query string. Quando a
  // opção já está selecionada (ex.: ?variant= na URL já a definiu no servidor), nada é
  // disparado — sem re-render, sem flick.
  function preselectVariantOptions(argilaSlug, tamanhoSlug) {
    var picker = document.querySelector('variant-selects');
    if (!picker) return;
    var fieldsets = picker.querySelectorAll('fieldset');
    for (var i = 0; i < fieldsets.length; i++) {
      var legend = fieldsets[i].querySelector('legend');
      if (!legend) continue;
      var want = null;
      if (/argila/i.test(legend.textContent)) want = argilaSlug;
      else if (/tamanho/i.test(legend.textContent)) want = tamanhoSlug;
      if (!want) continue;
      var radios = fieldsets[i].querySelectorAll('input[type="radio"]');
      for (var j = 0; j < radios.length; j++) {
        if (slugify(radios[j].value) === want) {
          if (!radios[j].checked) {
            radios[j].checked = true;
            radios[j].dispatchEvent(new Event('change', { bubbles: true }));
          }
          break;
        }
      }
    }
    var selects = picker.querySelectorAll('select');
    for (var k = 0; k < selects.length; k++) {
      var wrapper = selects[k].closest('.product-form__input');
      if (!wrapper) continue;
      var wantSel = null;
      if (/argila/i.test(wrapper.textContent)) wantSel = argilaSlug;
      else if (/tamanho/i.test(wrapper.textContent)) wantSel = tamanhoSlug;
      if (!wantSel) continue;
      var opts = selects[k].options;
      for (var m = 0; m < opts.length; m++) {
        if (slugify(opts[m].value) === wantSel) {
          if (selects[k].value !== opts[m].value) {
            selects[k].value = opts[m].value;
            selects[k].dispatchEvent(new Event('change', { bubbles: true }));
          }
          break;
        }
      }
    }
  }

  // Combinação oficial (metafields custom.foto_argila/foto_esmalte, expostos como
  // defaults no overlay da frase): a única cuja galeria mostra as fotos reais iniciais.
  function officialCombo() {
    var box = document.querySelector('[data-photo-info]');
    if (!box) return null;
    var argila = slugify(box.getAttribute('data-default-argila'));
    var esmalte = slugify(box.getAttribute('data-default-esmalte'));
    if (!argila || !esmalte) return null;
    return { argila: argila, esmalte: esmalte };
  }

  // Modo "solo": com uma combinação simulada ativa, a galeria mostra só a foto dela; as
  // fotos iniciais (reais) voltam sem seleção ou na combinação oficial. A classe vai na
  // seção para alcançar galeria, miniaturas e modal de zoom (component-glaze-media.css).
  function setSolo(on) {
    var gal = gallery();
    if (!gal) return;
    var scope = gal.closest('.shopify-section') || gal;
    scope.classList.toggle('amme-glaze-solo', !!on);
  }

  function isSolo() {
    var gal = gallery();
    if (!gal) return false;
    var scope = gal.closest('.shopify-section') || gal;
    return scope.classList.contains('amme-glaze-solo');
  }

  function showBase(gal) {
    setSolo(false);
    var first = gal.querySelector('.product__media-item:not([data-glaze-esmalte])');
    if (first && typeof gal.setActiveMedia === 'function') {
      gal.setActiveMedia(first.getAttribute('data-media-id'), true);
    }
    resetSliders(gal);
    updatePhotoInfo(null, null);
    scheduleUrlUpdate();
  }

  // Foto da combinação: prefere a específica do tamanho selecionado
  // (data-glaze-tamanho) e cai na genérica (sem tamanho) quando o tamanho não tem foto
  // própria. Fotos de OUTRO tamanho nunca casam.
  function findSlide(slides, argilaSlug, esmalteSlug, tamanhoSlug) {
    if (!argilaSlug || !esmalteSlug) return null;
    var generic = null;
    for (var i = 0; i < slides.length; i++) {
      if (
        slugify(slides[i].getAttribute('data-glaze-argila')) !== argilaSlug ||
        slugify(slides[i].getAttribute('data-glaze-esmalte')) !== esmalteSlug
      ) {
        continue;
      }
      var tamanho = slides[i].getAttribute('data-glaze-tamanho');
      if (tamanho) {
        if (tamanhoSlug && slugify(tamanho) === tamanhoSlug) return slides[i];
      } else if (!generic) {
        generic = slides[i];
      }
    }
    return generic;
  }

  function apply(argilaSlug, esmalteSlug) {
    var gal = gallery();
    if (!gal) return;

    var slides = gal.querySelectorAll('.product__media-item[data-glaze-esmalte]');
    if (!slides.length) return; // produto sem fotos de variação: não mexe na galeria

    // Combinação oficial selecionada: mostra as fotos reais iniciais, não a simulação.
    var official = officialCombo();
    if (official && official.argila === argilaSlug && official.esmalte === esmalteSlug) {
      clearActive();
      showBase(gal);
      return;
    }

    var match = findSlide(slides, argilaSlug, esmalteSlug, tamanhoFromVariantPicker());

    clearActive();

    if (!match) {
      showBase(gal);
      return;
    }

    // Foto de variação ativa é sempre simulação: galeria fica só com ela. A exceção da
    // combinação oficial (metafields) já saiu acima; produto sem metafields não tem
    // combinação oficial e qualquer seleção entra em solo.
    setSolo(true);
    match.classList.add(ACTIVE);
    promoteImage(match);
    updatePhotoInfo(displayArgila(argilaSlug), displayEsmalte(esmalteSlug), true);
    var mediaId = match.getAttribute('data-media-id'); // "<section.id>-<media.id>"
    var thumb = gal.querySelector('.thumbnail-list__item[data-target="' + mediaId + '"]');
    if (thumb) thumb.classList.add(ACTIVE);
    syncModal();

    if (typeof gal.setActiveMedia === 'function') gal.setActiveMedia(mediaId, true);
    resetSliders(gal);
    scheduleUrlUpdate();
  }

  function applyFromRadio(radio) {
    lastEsmalteSlug = slugify(radio.value);
    var argila = argilaFromRadio(radio) || argilaFromVariantPicker();
    apply(argila, lastEsmalteSlug);
  }

  // Troca de argila: transfere a cor para o grupo recém-visível — o mesmo esmalte quando
  // o grupo o tem, senão a primeira cor (ex.: Branco Amme). O change disparado re-entra
  // pelo onChange (galeria/frase) e deixa o radio marcado para o app ler como property.
  function syncGroupSelection(group) {
    if (!lastEsmalteSlug) return false; // nenhuma cor escolhida ainda: nada a transferir
    var radios = group.querySelectorAll('input[type="radio"]');
    if (!radios.length) return false;
    var target = null;
    for (var i = 0; i < radios.length; i++) {
      if (slugify(radios[i].value) === lastEsmalteSlug) {
        target = radios[i];
        break;
      }
    }
    if (!target) target = radios[0];
    enforceGroup = group;
    enforceSlug = slugify(target.value);
    enforceUntil = Date.now() + 4000;
    if (target.checked) return false; // seleção já correta: segue o fluxo normal
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  // Dentro da janela pós-switch: se a restauração tardia do app trocou a seleção,
  // re-marca o alvo do carry-over. Retorna true quando re-disparou o change.
  function enforceSelection(group) {
    if (group !== enforceGroup || Date.now() >= enforceUntil) return false;
    var checked = group.querySelector('input[type="radio"]:checked');
    if (checked && slugify(checked.value) === enforceSlug) return false;
    var radios = group.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      if (slugify(radios[i].value) === enforceSlug) {
        radios[i].checked = true;
        radios[i].dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    return false;
  }

  // Re-aplica o estado atual (troca de variante, alternância de grupo do app, primeira
  // renderização). Idempotente: sai cedo se a foto ativa já corresponde à seleção — o
  // observer do container do app dispara em rajadas e o setActiveMedia não é gratuito.
  function reapply() {
    var group = visibleGlazeGroup();

    // Cor da query string: marca o radio assim que o app renderiza o grupo (match
    // exato). Fica pendente até CONFIRMAR marcada — re-render do tema ou do app pode
    // apagar a marcação sem evento; o retry do init re-entra aqui até o deadline.
    if (group && pendingCor) {
      if (Date.now() > pendingCorUntil) {
        pendingCor = null;
      } else {
        var pendRadios = group.querySelectorAll('input[type="radio"]');
        for (var p = 0; p < pendRadios.length; p++) {
          if (slugify(pendRadios[p].value) === pendingCor) {
            enforceGroup = group;
            enforceSlug = pendingCor;
            enforceUntil = Date.now() + 4000;
            if (pendRadios[p].checked) {
              pendingCor = null; // confirmada: restauração concluída
            } else {
              pendRadios[p].checked = true;
              pendRadios[p].dispatchEvent(new Event('change', { bubbles: true }));
              return;
            }
            break;
          }
        }
        // radio ausente no grupo visível (transição de argila em curso): mantém pendente
      }
    }

    var switched = group && lastGroup && group !== lastGroup;
    if (group) lastGroup = group;
    if (switched && syncGroupSelection(group)) return; // o change disparado já sincronizou
    if (!switched && group && enforceSelection(group)) return;

    var checked = group && group.querySelector('input[type="radio"]:checked');
    var active = document.querySelector('.product__media-item.' + ACTIVE);

    if (checked) {
      var argila = argilaFromRadio(checked) || argilaFromVariantPicker();
      var esmalte = slugify(checked.value);
      lastEsmalteSlug = esmalte;
      var official = officialCombo();
      var isOfficial = official && official.argila === argila && official.esmalte === esmalte;
      var gal = gallery();
      // Na combinação oficial nenhuma foto de variação deveria estar ativa — nesse caso
      // NÃO sai no early-exit: cai no apply, que roteia para showBase (auto-correção).
      if (active && gal && !isOfficial) {
        var slides = gal.querySelectorAll('.product__media-item[data-glaze-esmalte]');
        if (active === findSlide(slides, argila, esmalte, tamanhoFromVariantPicker())) {
          return; // já sincronizado (inclusive o tamanho: genérica ativa ≠ específica desejada)
        }
      }
      // Combinação oficial já exibindo as fotos base (sem foto ativa e sem solo):
      // evita re-rodar showBase a cada rajada do observer do app.
      if (!active && !isSolo() && isOfficial) return;
      apply(argila, esmalte);
    } else if (active && !pendingCor) {
      // Esmalte deixou de estar selecionado (ex.: trocou a argila): volta à foto base.
      // Com cor pendente da URL, a foto pré-aplicada fica até o app renderizar o grupo.
      var gal = gallery();
      clearActive();
      if (gal) showBase(gal);
    }
  }

  // Sem reload, o app leva segundos para alternar o grupo de esmaltes após a troca de
  // variante. Observa o container do app: quando um grupo muda de style (show/hide),
  // re-aplica com debounce — cobre inclusive esmalte pré-selecionado pelo app no grupo
  // novo. Retorna false se o container ainda não existe.
  var reapplyTimer = null;
  function watchGlazeContainer() {
    var container = document.querySelector('.avpoptions-container__v2');
    if (!container) return false;
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var target = mutations[i].target;
        if (target.nodeType === 1 && target.matches && target.matches('.glaze-dropdown.avp-option')) {
          clearTimeout(reapplyTimer);
          reapplyTimer = setTimeout(reapply, 150);
          return;
        }
      }
    }).observe(container, { attributes: true, attributeFilter: ['style'], subtree: true });
    return true;
  }

  // Frase segue a navegação manual da galeria: is-active (miniaturas/desktop, setado
  // por setActiveMedia) e slideChanged (swipe/setas do slider mobile). Guard no próprio
  // viewer porque o tema re-renderiza a galeria na troca de variante — re-anexado pelo
  // callback do variantChange.
  function watchGallery() {
    var gal = gallery();
    if (!gal) return;
    var viewer = gal.querySelector('[id^="GalleryViewer"]');
    if (!viewer || viewer.__ammePhotoInfoHooks) return;
    viewer.__ammePhotoInfoHooks = true;

    viewer.addEventListener('slideChanged', function (event) {
      var slide = event.detail && event.detail.currentElement;
      if (slide) updatePhotoInfoForSlide(slide);
    });

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var target = mutations[i].target;
        if (
          target.nodeType === 1 &&
          target.matches &&
          target.matches('.product__media-item.is-active')
        ) {
          updatePhotoInfoForSlide(target);
          return;
        }
      }
    }).observe(viewer, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  function onChange(event) {
    var target = event.target;
    if (!target || target.type !== 'radio') return;
    if (!target.closest || !target.closest('.glaze-dropdown.avp-option')) return;
    if (event.isTrusted) {
      enforceUntil = 0; // escolha real do usuário encerra o reforço
      pendingCor = null; // e a restauração pendente da URL
    }
    applyFromRadio(target);
  }

  function init() {
    // O quick-add do Dawn re-injeta e re-executa este script: liga os hooks uma vez por
    // página. Os handlers consultam o DOM vivo, então valem também para galerias
    // injetadas depois (quick-add).
    if (window.__ammeGlazeSync) return;
    window.__ammeGlazeSync = true;

    document.addEventListener('change', onChange, true);

    // Sem reload, o esmalte escolhido numa argila anterior continua marcado no grupo
    // oculto e o app o injetaria como property fantasma (chave vazia) no add-to-cart.
    // Desmarca radios de grupos ocultos no submit, em captura — antes de o tema/app
    // montarem o payload. A "memória" da seleção por argila é preservada até o submit.
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || !form.querySelector || !form.querySelector('input[name="id"]')) return;
        var groups = document.querySelectorAll('.glaze-dropdown.avp-option');
        for (var i = 0; i < groups.length; i++) {
          if (groups[i].offsetParent === null) {
            var checked = groups[i].querySelectorAll('input[type="radio"]:checked');
            for (var j = 0; j < checked.length; j++) checked[j].checked = false;
          }
        }
      },
      true
    );

    // Troca de variante (argila): o tema re-renderiza a galeria na hora; o app alterna o
    // grupo de esmaltes segundos depois (coberto pelo watchGlazeContainer). Este fallback
    // curto limpa/re-sincroniza o estado do tema.
    // PUB_SUB_EVENTS é `const` de topo (constants.js): existe como binding global mas
    // NÃO como window.PUB_SUB_EVENTS — o acesso precisa ser pelo identificador.
    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        setTimeout(function () {
          watchGallery();
          reapply();
          scheduleUrlUpdate(); // o tema reescreveu a URL só com ?variant=
        }, 300);
      });
    }

    // O observer do grupo de esmaltes sustenta o carry-over da cor na troca de argila,
    // inclusive em produto SEM fotos de variação (as partes de galeria do reapply saem
    // cedo sozinhas). Espera o app renderizar o grupo e desliga; o timeout evita
    // observer eterno quando o app não renderiza.
    // Restaura a combinação da query string: variante no picker do tema (no-op quando o
    // ?variant= já a definiu no servidor), cor agendada para quando o app renderizar e
    // foto da combinação aplicada JÁ — sem esperar o app (anti-flick).
    if (window.URLSearchParams) {
      var urlParams = new URLSearchParams(window.location.search);
      var corParam = slugify(urlParams.get('cor'));
      var argilaParam = slugify(urlParams.get('argila'));
      var tamanhoParam = slugify(urlParams.get('tamanho'));
      // Sem ?variant= nem ?argila=: o servidor renderiza a primeira variante (flocos),
      // mas as fotos iniciais são da combinação oficial — o picker abre na argila dela.
      if (!argilaParam && !urlParams.get('variant')) {
        var infoBox = document.querySelector('[data-photo-info]');
        if (infoBox) argilaParam = slugify(infoBox.getAttribute('data-default-argila'));
      }
      if (argilaParam || tamanhoParam) preselectVariantOptions(argilaParam, tamanhoParam);
      if (corParam) {
        lastEsmalteSlug = corParam;
        pendingCor = corParam;
        pendingCorUntil = Date.now() + 12000;
        apply(argilaParam || argilaFromVariantPicker(), corParam);
        // Retry ativo: os re-renders do tema/app nem sempre emitem mutation observável;
        // insiste até a marcação confirmar ou o deadline vencer.
        var pendingTimer = setInterval(function () {
          if (!pendingCor || Date.now() > pendingCorUntil) {
            clearInterval(pendingTimer);
            return;
          }
          reapply();
        }, 700);
      }
    }

    if (visibleGlazeGroup()) {
      watchGlazeContainer();
      reapply();
    } else {
      var observer = new MutationObserver(function () {
        if (visibleGlazeGroup()) {
          observer.disconnect();
          watchGlazeContainer();
          reapply();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () {
        observer.disconnect();
      }, 20000);
    }

    // Sincronização do modal de zoom e da frase por navegação manual: só interessa
    // quando a página tem fotos de variação.
    var gal = gallery();
    if (!gal || !gal.querySelector('.product__media-item[data-glaze-esmalte]')) return;

    watchGallery();

    // Re-sincroniza o modal ao abrir: cobre reescritas do innerHTML do modal que
    // aconteçam depois do reapply (ex.: re-render tardio disparado pelo app).
    var modal = document.querySelector('product-modal');
    if (modal) {
      new MutationObserver(function () {
        if (modal.hasAttribute('open')) syncModal();
      }).observe(modal, { attributes: true, attributeFilter: ['open'] });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
